import { Outlet, createRootRoute, createRoute, createRouter } from '@tanstack/react-router';
import { useEffect, type ReactNode } from 'react';
import { useAuth } from './auth/AuthProvider';
import { useQuery } from '@tanstack/react-query';
import { AppShell } from './components/AppShell';
import { fetchMe } from './queries/account';
import { Wordmark } from './components/Clapper';
import { AppLink, useGo, useRouteParams, usePathname } from './components/Nav';
import { Button, Spinner } from './components/ui';
import { canonicalUrl, onCustomDomain, servedOnCustomDomain } from './lib/env';
import { LoginPage, SignupPage } from './routes/AuthPages';
import { DashboardPage } from './routes/DashboardPage';
import { DesktopPage } from './routes/DesktopPage';
import { DevicePage } from './routes/DevicePage';
import { DownloadPage } from './routes/DownloadPage';
import { EditPage } from './routes/EditPage';
import { InvitePage } from './routes/InvitePage';
import { LinksPage } from './routes/LinksPage';
import { ForgotPasswordPage, ResetPasswordPage } from './routes/PasswordPages';
import { RequestPage } from './routes/RequestPage';
import { BrandingPage } from './routes/BrandingPage';
import { ConfigurationPage } from './routes/ConfigurationPage';
import { InboxPage } from './routes/InboxPage';
import { NotificationsPage } from './routes/NotificationsPage';
import { DomainPage } from './routes/DomainPage';
import { SettingsPage } from './routes/SettingsPage';
import { StartPage } from './routes/StartPage';
import { SubscriptionPage } from './routes/SubscriptionPage';
import { WelcomePage } from './routes/WelcomePage';
import { SharePage } from './routes/SharePage';

/**
 * Routes, defined in code. No generated route tree and no file-based
 * convention: the app has thirteen routes, and a hand-written tree you can
 * read top to bottom beats a build step.
 *
 * Path params are read with `useRouteParams()` (src/components/Nav.tsx) rather
 * than each route's own `useParams`, which would make every route object's
 * type depend on its own component and its component depend on the route.
 */

function FullPage({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-4 bg-slate-950 px-6 text-center">
      {children}
    </div>
  );
}

function Restoring() {
  return (
    <FullPage>
      <Spinner size={22} className="text-chalk-faint" />
    </FullPage>
  );
}

function NotFound() {
  return (
    <FullPage>
      <Wordmark />
      <h1 className="text-h2 text-chalk">There is nothing at this address</h1>
      <p className="max-w-sm text-body text-chalk-dim">
        The link may be mistyped, or whatever was here has been deleted.
      </p>
      <AppLink to="/">
        <Button variant="primary">Go to your library</Button>
      </AppLink>
    </FullPage>
  );
}

/**
 * Everything behind an account. Bounces to /login once we know there is none.
 *
 * `chrome` is how a route opts out of the app shell. Exactly one does: /new is
 * a full screen mode rather than a page, and a nav bar offering to take you
 * somewhere else while you are mid-take is the opposite of what that route is
 * for.
 */
function RequireAuth({ children, chrome = true }: { children: ReactNode; chrome?: boolean }) {
  const { user, loading } = useAuth();
  const go = useGo();
  const pathname = usePathname();
  // Whether the welcome steps are done comes from the account row, so a
  // brand-new account is welcomed whichever way it signed in, and an
  // account made again after being deleted is welcomed again.
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe, enabled: Boolean(user), staleTime: 60_000 });
  // The welcome page itself, and an invitation, which the steps come after.
  const exempt = pathname === '/welcome' || pathname.startsWith('/invite/');
  const welcome = Boolean(user) && me.data?.onboardedAt === null && !exempt;

  useEffect(() => {
    if (!loading && !user) go('/login', { replace: true });
  }, [go, loading, user]);
  useEffect(() => {
    if (!welcome) return;
    const next = `${pathname}${window.location.search}`;
    go(next === '/' ? '/welcome' : `/welcome?next=${encodeURIComponent(next)}`, { replace: true });
  }, [go, pathname, welcome]);

  if (loading || !user || (me.isPending && !exempt) || welcome) return <Restoring />;
  return chrome ? <AppShell>{children}</AppShell> : <>{children}</>;
}

/**
 * A team's own domain serves share pages and nothing else.
 *
 * main.tsx catches the first load; this catches everything after it, since a
 * link inside the app navigates client-side and never reloads the page. Both
 * are needed: without the first there is a flash of an app that does not
 * belong on that domain, and without this one a single link out of the share
 * page lands somebody on a sign-in form that cannot work there.
 */
function OffCustomDomain() {
  const pathname = usePathname();
  useEffect(() => {
    if (!onCustomDomain() || servedOnCustomDomain(pathname)) return;
    window.location.replace(canonicalUrl(`${pathname}${window.location.search}`));
  }, [pathname]);
  if (onCustomDomain() && !servedOnCustomDomain(pathname)) return <Restoring />;
  return <Outlet />;
}

const rootRoute = createRootRoute({
  component: OffCustomDomain,
});

// ---- public ---------------------------------------------------------------

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  component: SignupPage,
});

// Asking for a reset link, and the page the link in the email lands on. Both
// public: somebody who has forgotten their password has no session to check.
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
});

const shareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/v/$shareId',
  component: function ShareRoute() {
    const { shareId } = useRouteParams();
    return shareId ? <SharePage shareId={shareId} /> : <NotFound />;
  },
});

const requestRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/r/$token',
  component: function RequestRoute() {
    const { token } = useRouteParams();
    return token ? <RequestPage token={token} /> : <NotFound />;
  },
});

// The invite page has to work for somebody with no account yet, so it handles
// its own signed-out state instead of sitting behind RequireAuth.
const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite/$token',
  component: function InviteRoute() {
    const { token } = useRouteParams();
    return token ? <InvitePage token={token} /> : <NotFound />;
  },
});

// Where the email from the Slate team lands somebody we set up ourselves,
// comped. Public like the invite page, and for the same reason: the person
// has never signed in. Once they are, it hands them to the library, and the
// welcome steps on the way.
const startRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/start/$token',
  component: function StartRoute() {
    const { token } = useRouteParams();
    return token ? <StartPage token={token} /> : <NotFound />;
  },
});

// Authorizing the desktop app. Public because the person may need to sign in
// first, and the page handles that itself; the decision endpoint behind it is
// what actually requires a session.
const deviceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/device',
  component: function DeviceRoute() {
    const search = new URLSearchParams(window.location.search);
    return <DevicePage code={search.get('code') ?? undefined} />;
  },
});

// Every way to record: the two desktop apps, the extension, the phone apps and
// the browser itself. Public, because the person may be here before they have
// an account.
const downloadRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/download',
  component: DownloadPage,
});

// Signing the desktop app in by bouncing through the browser. Public for the
// same reason as /device: the page takes you through login itself.
const desktopRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/desktop',
  component: function DesktopRoute() {
    const search = new URLSearchParams(window.location.search);
    const state = search.get('state');
    return state ? (
      <DesktopPage
        state={state}
        signup={search.get('signup') === '1'}
        scheme={search.get('scheme') ?? 'slate'}
        app={search.get('app') ?? undefined}
        redirect={search.get('redirect') ?? undefined}
      />
    ) : (
      <NotFound />
    );
  },
});

// ---- signed in ------------------------------------------------------------

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: function DashboardRoute() {
    return (
      <RequireAuth>
        <DashboardPage />
      </RequireAuth>
    );
  },
});


const linksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/links',
  component: function LinksRoute() {
    return (
      <RequireAuth>
        <LinksPage />
      </RequireAuth>
    );
  },
});

const inboxRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inbox',
  component: function InboxRoute() {
    return (
      <RequireAuth>
        <InboxPage />
      </RequireAuth>
    );
  },
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: function SettingsRoute() {
    return (
      <RequireAuth>
        <SettingsPage />
      </RequireAuth>
    );
  },
});

const subscriptionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/subscription',
  component: function SubscriptionRoute() {
    return (
      <RequireAuth>
        <SubscriptionPage />
      </RequireAuth>
    );
  },
});

const brandingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/branding',
  component: function BrandingRoute() {
    return (
      <RequireAuth>
        <BrandingPage />
      </RequireAuth>
    );
  },
});

const domainRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/domain',
  component: function DomainRoute() {
    return (
      <RequireAuth>
        <DomainPage />
      </RequireAuth>
    );
  },
});

const configurationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/configuration',
  component: function ConfigurationRoute() {
    return (
      <RequireAuth>
        <ConfigurationPage />
      </RequireAuth>
    );
  },
});

const notificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/notifications',
  component: function NotificationsRoute() {
    return (
      <RequireAuth>
        <NotificationsPage />
      </RequireAuth>
    );
  },
});

// Right after signup: pick a plan.
const welcomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/welcome',
  // No shell: there is no library to navigate yet, and the steps deserve
  // the whole screen.
  component: function WelcomeRoute() {
    return (
      <RequireAuth chrome={false}>
        <WelcomePage />
      </RequireAuth>
    );
  },
});


// The editor is a full-screen mode like /new: no app shell.
const editRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/edit/$videoId',
  component: function EditRoute() {
    const { videoId } = useRouteParams();
    return (
      <RequireAuth chrome={false}>
        {videoId ? <EditPage videoId={videoId} /> : <NotFound />}
      </RequireAuth>
    );
  },
});

// Starting a team happens on the Subscription tab now; old links land there.
// Declared before `/org/$slug`; static segments outrank params in the matcher
// regardless, but the ordering documents the intent.
const orgNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/org/new',
  component: function OrgNewRoute() {
    const go = useGo();
    useEffect(() => {
      go('/settings/subscription', { replace: true });
    }, [go]);
    return null;
  },
});

// The org pages are gone: the team's recordings are the Library's team shelf
// and its people are on the Subscription tab. Old links land there.
const orgRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/org/$slug',
  component: function OrgRoute() {
    const go = useGo();
    useEffect(() => {
      go('/', { replace: true });
    }, [go]);
    return null;
  },
});

const orgMembersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/org/$slug/members',
  component: function OrgMembersRoute() {
    const go = useGo();
    useEffect(() => {
      go('/settings/subscription', { replace: true });
    }, [go]);
    return null;
  },
});

// Billing moved into Settings; old links and Stripe return URLs still land.
const orgBillingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/org/$slug/billing',
  component: function OrgBillingRoute() {
    const go = useGo();
    useEffect(() => {
      go(`/settings/subscription${window.location.search}`, { replace: true });
    }, [go]);
    return null;
  },
});

const routeTree = rootRoute.addChildren([
  dashboardRoute,
  loginRoute,
  signupRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  linksRoute,
  inboxRoute,
  settingsRoute,
  subscriptionRoute,
  brandingRoute,
  configurationRoute,
  notificationsRoute,
  domainRoute,
  welcomeRoute,
  editRoute,
  shareRoute,
  requestRoute,
  inviteRoute,
  startRoute,
  deviceRoute,
  desktopRoute,
  downloadRoute,
  orgNewRoute,
  orgRoute,
  orgMembersRoute,
  orgBillingRoute,
]);

export const router = createRouter({
  routeTree,
  defaultNotFoundComponent: NotFound,
});
